import { Fragment } from 'react';

import { FilterListItem } from '@components/filter-list-item';
import { AnimatedRadio } from '@components/radio-group/animated-radio';
import { Slider, MultiSlider } from '@components/slider';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import type { MenuProps } from './types';

import { FilterActionButtons } from '../actions-buttons';
import { FilterComparer } from '../common';

const DEFAULT_PROGRESS_COMPARERS = [
  'is',
  'not_is',
  'gt',
  'lt',
  'between',
] satisfies Array<FilterComparer>;

export const ProgressMenu = ({ state, actions, config }: MenuProps) => {
  const { t } = useScopedTranslate(locale);
  const comparers =
    ('allowedComparers' in config && config.allowedComparers) || DEFAULT_PROGRESS_COMPARERS;

  return (
    <Fragment>
      {comparers.map((comparer) => {
        const isCurrentComparer = state.comparer === comparer;
        return (
          <Fragment key={comparer}>
            <FilterListItem
              icon={<AnimatedRadio checked={isCurrentComparer} />}
              key={comparer}
              label={t(`core.comparator.${comparer}`) || comparer}
              onClick={() => {
                const eitherIsBetween = state.comparer === 'between' || comparer === 'between';
                actions.selectComparer(comparer);

                if (!eitherIsBetween) {
                  actions.selectValue(state.value ?? '0.5');
                } else if (comparer === 'between') {
                  actions.selectValue(['0', '1']);
                }
              }}
            />
            {isCurrentComparer && (
              <div style={{ margin: `0 ${pxToRem(8)}` }}>
                {comparer === 'between' ? (
                  <MultiSlider
                    label={t('progress')}
                    defaultValue={
                      state.value
                        ? [Number(state.value[0]) * 100, Number(state.value[1]) * 100]
                        : undefined
                    }
                    onChange={(value) => {
                      actions.selectValue([String(value[0] / 100), String(value[1] / 100)]);
                    }}
                    minValue={0}
                    maxValue={100}
                    formatDisplayValue={(value) => `${value}%`}
                  />
                ) : (
                  <Slider
                    label={t('progress')}
                    defaultValue={state.value ? Number(state.value) * 100 : undefined}
                    onChange={(value) => {
                      actions.selectValue(String(value / 100));
                    }}
                    minValue={0}
                    maxValue={100}
                    formatDisplayValue={(value) => `${value}%`}
                  />
                )}
              </div>
            )}
          </Fragment>
        );
      })}

      <FilterActionButtons
        state={state}
        actions={actions}
        disableApply={!state.comparer || !state.value}
      />
    </Fragment>
  );
};

const locale = {
  en: {
    progress: 'Progress',
  },
  is: {
    progress: 'Framvinda',
  },
};
